(function () {
  const rootState = new WeakMap();

  function formatMoney(price) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(price, window.theme?.moneyFormat || undefined);
      }
    } catch (e) {}
    return (Number(price || 0) / 100).toFixed(2) + ' €';
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function normalizePredictiveBase(url) {
    const base = url || '/search/suggest';
    return base.endsWith('.json') ? base : `${base}.json`;
  }

  function getRootState(root) {
    if (!rootState.has(root)) {
      rootState.set(root, {
        debounceTimer: null,
        abortController: null
      });
    }
    return rootState.get(root);
  }

  function getElements(root) {
    return {
      input: root.querySelector('[data-gg-inline-search-input]'),
      clearBtn: root.querySelector('[data-gg-inline-search-clear]'),
      dropdown: root.querySelector('[data-gg-inline-search-dropdown]'),
      defaultBox: root.querySelector('[data-gg-inline-search-default]'),
      loading: root.querySelector('[data-gg-inline-search-loading]'),
      results: root.querySelector('[data-gg-inline-search-results]')
    };
  }

  function openDropdown(root) {
    const { input, dropdown } = getElements(root);
    if (!input || !dropdown) return;
    dropdown.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function closeDropdown(root) {
    const { input, dropdown, loading, results, defaultBox } = getElements(root);
    if (!input || !dropdown || !loading || !results || !defaultBox) return;
    dropdown.hidden = true;
    loading.hidden = true;
    results.hidden = true;
    defaultBox.hidden = false;
    input.setAttribute('aria-expanded', 'false');
  }

  function showDefault(root) {
    const { defaultBox, loading, results } = getElements(root);
    if (!defaultBox || !loading || !results) return;
    openDropdown(root);
    defaultBox.hidden = false;
    loading.hidden = true;
    results.hidden = true;
  }

  function showLoading(root) {
    const { defaultBox, loading, results } = getElements(root);
    if (!defaultBox || !loading || !results) return;
    openDropdown(root);
    defaultBox.hidden = true;
    loading.hidden = false;
    results.hidden = true;
  }

  function getProductImage(product) {
    if (product?.featured_image?.url) return product.featured_image.url;
    if (product?.image) return product.image;
    if (product?.featured_media?.preview_image?.src) return product.featured_media.preview_image.src;
    return '';
  }

  function getFirstVariantId(product) {
    if (product?.variants?.length && product.variants[0]?.id) {
      return product.variants[0].id;
    }
    return null;
  }

  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const count = Number(cart.item_count || 0);

      document.querySelectorAll('.cart-count, .cart-count-bubble').forEach((el) => {
        el.textContent = count;
        if (count > 0) {
          el.hidden = false;
          el.style.display = '';
        } else {
          el.hidden = true;
        }
      });
    } catch (e) {}
  }

  async function addToCart(variantId, button) {
    if (!variantId || !button) return;
    const originalText = button.innerHTML;

    try {
      button.disabled = true;
      button.classList.add('is-loading');
      button.innerHTML = 'Ajout...';

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1
        })
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      await refreshCartCount();

      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.innerHTML = 'Ajouté ✓';

      setTimeout(() => {
        button.disabled = false;
        button.classList.remove('is-added');
        button.innerHTML = originalText;
      }, 1200);
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.classList.add('is-error');
      button.innerHTML = 'Erreur';

      setTimeout(() => {
        button.classList.remove('is-error');
        button.innerHTML = originalText;
      }, 1400);
    }
  }

  function renderEmpty(root, query) {
    const { results, loading, defaultBox } = getElements(root);
    if (!results || !loading || !defaultBox) return;
    const searchUrl = root.dataset.searchUrl || '/search';

    results.innerHTML = `
      <div class="gg-inline-search__empty">
        <strong>Aucun résultat trouvé</strong>
        <p>Essaie une marque, une saveur ou une catégorie.</p>
        <div class="gg-inline-search__footer">
          <a class="gg-inline-search__view-all" href="${searchUrl}?q=${encodeURIComponent(query)}&type=product,page,article,collection">
            Voir tous les résultats pour "${escapeHtml(query)}"
          </a>
        </div>
      </div>
    `;

    loading.hidden = true;
    defaultBox.hidden = true;
    results.hidden = false;
  }

  function renderResults(root, data, query) {
    const { results, loading, defaultBox } = getElements(root);
    if (!results || !loading || !defaultBox) return;

    const searchUrl = root.dataset.searchUrl || '/search';
    const resources = data?.resources?.results || {};
    const queries = resources.queries || [];
    const products = resources.products || [];
    const collections = resources.collections || [];
    const pages = resources.pages || [];
    const articles = resources.articles || [];
    const extraLinks = [...collections, ...pages, ...articles].slice(0, 4);

    if (!(queries.length || products.length || extraLinks.length)) {
      renderEmpty(root, query);
      return;
    }

    const queriesHtml = queries.length
      ? `
        <div class="gg-inline-search__queries">
          <p class="gg-inline-search__heading">Suggestions</p>
          <div class="gg-inline-search__query-list">
            ${queries.map((item) => `
              <a class="gg-inline-search__query-item" href="${item.url}">${escapeHtml(item.text)}</a>
            `).join('')}
          </div>
        </div>
      `
      : '';

    const productsHtml = products.length
      ? `
        <div class="gg-inline-search__products">
          <p class="gg-inline-search__heading">Produits</p>
          <div class="gg-inline-search__product-list">
            ${products.slice(0, 6).map((product) => {
              const image = getProductImage(product);
              const variantId = getFirstVariantId(product);
              const priceHtml = product.price ? `<div class="gg-inline-search__product-price">${formatMoney(product.price)}</div>` : '';
              const buttonHtml = variantId
                ? `<button type="button" class="gg-inline-search__add" data-gg-add-to-cart data-variant-id="${variantId}">Ajouter</button>`
                : '';

              return `
                <div class="gg-inline-search__product-card">
                  <a class="gg-inline-search__product" href="${product.url}">
                    <div class="gg-inline-search__product-image-wrap">
                      ${image ? `<img class="gg-inline-search__product-image" src="${image}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
                    </div>
                    <div class="gg-inline-search__product-title">${escapeHtml(product.title)}</div>
                    ${priceHtml}
                  </a>
                  ${buttonHtml}
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `
      : '';

    const extraHtml = extraLinks.length
      ? `
        <div class="gg-inline-search__extra">
          <p class="gg-inline-search__heading">Autres résultats</p>
          <div class="gg-inline-search__extra-links">
            ${extraLinks.map((item) => `<a href="${item.url}">${escapeHtml(item.title)}</a>`).join('')}
          </div>
        </div>
      `
      : '';

    results.innerHTML = `
      <div class="gg-inline-search__live-grid">
        ${queriesHtml}
        ${productsHtml}
        ${extraHtml}
      </div>
      <div class="gg-inline-search__footer">
        <a class="gg-inline-search__view-all" href="${searchUrl}?q=${encodeURIComponent(query)}&type=product,page,article,collection">
          Voir tous les résultats pour "${escapeHtml(query)}"
        </a>
      </div>
    `;

    loading.hidden = true;
    defaultBox.hidden = true;
    results.hidden = false;
  }

  async function fetchPredictive(root, query) {
    const state = getRootState(root);
    const predictiveBase = normalizePredictiveBase(root.dataset.predictiveSearchUrl);

    if (state.abortController) state.abortController.abort();
    state.abortController = new AbortController();

    showLoading(root);

    const connector = predictiveBase.includes('?') ? '&' : '?';
    const url =
      `${predictiveBase}${connector}` +
      `q=${encodeURIComponent(query)}` +
      `&resources[type]=query,product,collection,page,article` +
      `&resources[limit]=8` +
      `&resources[limit_scope]=each` +
      `&resources[options][unavailable_products]=hide` +
      `&resources[options][fields]=title,product_type,variants.title,tag`;

    try {
      const response = await fetch(url, {
        signal: state.abortController.signal,
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      renderResults(root, data, query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      const { results, loading, defaultBox } = getElements(root);
      if (!results || !loading || !defaultBox) return;
      loading.hidden = true;
      defaultBox.hidden = true;
      results.hidden = false;
      results.innerHTML = `
        <div class="gg-inline-search__empty">
          <strong>Erreur de chargement</strong>
          <p>Impossible de récupérer les résultats pour le moment.</p>
        </div>
      `;
    }
  }

  function handleInput(root) {
    const state = getRootState(root);
    const { input, clearBtn } = getElements(root);
    if (!input) return;

    const query = input.value.trim();
    if (clearBtn) clearBtn.hidden = !query;

    clearTimeout(state.debounceTimer);

    if (query.length < 2) {
      showDefault(root);
      return;
    }

    state.debounceTimer = setTimeout(() => {
      fetchPredictive(root, query);
    }, 180);
  }

  document.addEventListener('focusin', (event) => {
    const input = event.target.closest('[data-gg-inline-search-input]');
    if (!input) return;
    const root = input.closest('[data-gg-inline-search]');
    if (!root) return;

    if (!input.value.trim()) {
      showDefault(root);
    } else {
      handleInput(root);
    }
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest('[data-gg-inline-search-input]');
    if (!input) return;
    const root = input.closest('[data-gg-inline-search]');
    if (!root) return;
    handleInput(root);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-gg-inline-search]').forEach(closeDropdown);
  });

  document.addEventListener('click', (event) => {
    const clearBtn = event.target.closest('[data-gg-inline-search-clear]');
    if (clearBtn) {
      const root = clearBtn.closest('[data-gg-inline-search]');
      const { input, clearBtn: btn } = getElements(root);
      if (input) input.value = '';
      if (btn) btn.hidden = true;
      showDefault(root);
      if (input) input.focus();
      return;
    }

    const addBtn = event.target.closest('[data-gg-add-to-cart]');
    if (addBtn) {
      const variantId = addBtn.getAttribute('data-variant-id');
      addToCart(variantId, addBtn);
      return;
    }

    const insideRoot = event.target.closest('[data-gg-inline-search]');
    document.querySelectorAll('[data-gg-inline-search]').forEach((root) => {
      if (insideRoot !== root) closeDropdown(root);
    });
  });
})();
